// Shared helpers + vocabulary for the Morning Huddle (ประชุมเช้า) feature.
//
// The huddle STORES only what a manager authors (goals / announcements / notes),
// via /api/huddle → Xano daily_huddle. The operational topics from SOP-FO-002
// (today's queue, who to confirm, notable cases) are NOT stored — they are
// COMPOSED at read time from the live /api/appointments?type=dashboard feed.
// These helpers back both sides.

import { scaleStatus } from './scale.js'

const BKK = 7 * 3600 * 1000
const pad = (n) => String(n).padStart(2, '0')

export const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))

// Branches that never run a morning huddle → kept out of the branch picker.
// สนามเป้า is the head office, not a treating clinic.
export const HUDDLE_EXCLUDED_BRANCH_NAMES = ['สนามเป้า']
export const isHuddleBranchExcluded = (name) =>
  HUDDLE_EXCLUDED_BRANCH_NAMES.some((n) => String(name || '').includes(n))

// Local Bangkok "YYYY-MM-DD" for a given epoch (defaults to now). Used as the
// huddle_date scope key and the appointment day filter — must match the app's
// day-bucketing, which is Asia/Bangkok.
export function todayBKK(ms = Date.now()) {
  const d = new Date(Number(ms) + BKK)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/* ── the branch/date filter, sticky for one clinic day ────────────────────── */
//
// Daily Goal opens on today at the employee's own branch — but a manager who
// switches to another branch is walking back and forth between this page and a
// patient all morning, and re-defaulting on every return means re-picking the
// branch every time. So the pair is remembered, and remembered ONLY within the
// Bangkok day it was chosen on: tomorrow's first visit starts clean rather than
// silently showing yesterday.
//
// Keyed by user, because a shared front-desk browser must not hand the next
// person the previous manager's branch — the huddle they would then read is a
// different clinic's.
const SCOPE_PREFIX = 'ss:huddleScope:'
const scopeKey = (userKey) => SCOPE_PREFIX + (userKey || 'anon')

export const huddleUserKey = (me) =>
  me?.email || (me?.employee_id ? `emp:${me.employee_id}` : '')

// The stored pair, or null when there is nothing to restore — no entry, another
// user's, or another day's. Null means "open on the defaults".
export function readHuddleScope(userKey, day) {
  try {
    const s = JSON.parse(localStorage.getItem(scopeKey(userKey)) || 'null')
    if (!s || s.day !== day || !s.branch_id) return null
    return { branchId: String(s.branch_id), date: String(s.date || day) }
  } catch { return null }   // private mode / bad JSON → the defaults are fine
}

export function writeHuddleScope(userKey, day, branchId, date) {
  if (!branchId) return    // '' is "not resolved yet", not a choice worth keeping
  try {
    localStorage.setItem(scopeKey(userKey), JSON.stringify({
      day, branch_id: String(branchId), date: String(date || day),
    }))
  } catch { /* ignore quota / private mode */ }
}

// Minutes-since-midnight (Bangkok) for an appointment start/end stored as epoch ms.
export const bkkMin = (ms) => { const d = new Date(Number(ms) + BKK); return d.getUTCHours() * 60 + d.getUTCMinutes() }
export const hm = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`

const TH_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
const TH_DAYS = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
export function thaiDate(dateStr) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(dateStr || ''))
  if (!m) return dateStr || '—'
  const [, y, mo, d] = m.map(Number)
  const dt = new Date(y, mo - 1, d)
  return `${TH_DAYS[dt.getDay()]} ${d} ${TH_MONTHS[mo - 1]} ${y + 543}`
}

const TH_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// Compact Thai date for an inline mention: "11 ก.ย.". The พ.ศ. year is appended
// (2 digits) ONLY when it differs from `ref` — inside a day's queue every date is
// this year, and printing "2569" on every row is noise.
// Accepts what Xano hands back for an appointment: "YYYY-MM-DD" or epoch ms.
export function thaiDateShort(v, ref = todayBKK()) {
  const iso = typeof v === 'number'
    ? new Date(v + BKK).toISOString().slice(0, 10)
    : String(v ?? '').slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return ''
  const [, y, mo, d] = m
  const short = `${Number(d)} ${TH_MONTHS_SHORT[Number(mo) - 1] || ''}`
  return y === String(ref).slice(0, 4) ? short : `${short} ${String(Number(y) + 543).slice(-2)}`
}

/* -------------------------------------------------------------------------- */
/* Appointment status → huddle buckets (codes: data/appointmentStatuses.js).   */
/* -------------------------------------------------------------------------- */
// Status labels and badge colours live in data/appointmentStatuses.js. Re-exported
// here because half the huddle UI imports everything it needs from this module.
export { STATUS_TH, codeCls } from '../data/appointmentStatuses.js'

// คิวนัดทั้งหมด shows only confirmed appointments (C / C*); ต้องยืนยัน shows
// everything that is NOT confirmed.
export const isConfirmedStatus = (s) => s === 'C' || s === 'C*'

// Heuristic for the "เคสน่าสนใจ / High-Billing" topic: the appointment item has
// no price, so we flag by treatment type/detail keywords (Implant / Invisalign /
// veneer / จัดฟัน / รากฟันเทียม). Best-effort, clearly labelled auto-flagging.
const HIGH_VALUE_RE = /(implant|invisalign|veneer|aligner|จัดฟัน|รากฟันเทียม|วีเนียร์|ครอบฟัน|รากเทียม)/i
export const isHighValue = (text) => HIGH_VALUE_RE.test(String(text || ''))

/**
 * Turn the raw dashboard payload into the huddle's live sections.
 * Filters to real patient appointments (drops doctor blocks / timetable events).
 * Each appt carries patientId (for upsell) and scale (last-scaling status).
 */
export function composeQueue(rawRows) {
  const appts = asList(rawRows)
    .filter((a) => !a.event_type && a.block !== true)
    .map((a) => {
      const p = a._patient || {}
      const type = [(a._appointmentList?.type || '').trim(), (a._appointmentList?.detail || '').trim()].filter(Boolean).join(' · ')
      const note = (a.details || '').trim()
      return {
        id: Number(a.id) || 0,                       // appointment id — the upsell plan hangs off it
        s: bkkMin(a.start_date),
        patientId: Number(a.patient_id) || 0,
        patient: [p.name, p.nickname && `(${p.nickname})`].filter(Boolean).join(' ').trim() || (a.title || '').trim() || '—',
        hn: p.HN || '',
        patientStatus: (p.status || '').trim(),
        status: a.status || '',
        doctor: a.doctor?.code || '',
        type,
        note,
        remark: (a.remark || '').trim(),
        highValue: isHighValue(`${type} ${note} ${a.remark || ''}`),
        scale: (Number(a.patient_id) || 0) > 0 ? scaleStatus(a) : null,
        nextAppt: nextApptOf(a),
      }
    })
    .sort((x, y) => x.s - y.s)

  return deriveQueue(appts)
}

// The patient's next booking AFTER this row's day, from the Xano addon
// "appointment next active" (api 445 type=dashboard). null = nothing booked.
//
// `status` is very often blank: a slot booked weeks out carries no status until
// someone works through the confirm list, so only tomorrow-ish rows tend to have
// C/C*/P. Render the code only when it is there rather than inventing one.
//
// NB this is strictly LATER DAYS. POST /patient/status counts a future slot from
// `now`, so an appointment later TODAY satisfies มีนัดแล้ว upstream while showing
// as "ไม่มีนัดถัดไป" here. Deliberate — "next appointment" that returned today's
// own row would be useless on every line.
function nextApptOf(a) {
  const n = a?._next_appointment
  const date = n?.date || n?.start_date
  return date ? { date, status: (n.status || '').trim() } : null
}

// The three tab buckets, derived from one appointment list. Split out of
// composeQueue so a local edit (a status change) can rebuild them without
// refetching the day.
function deriveQueue(appts) {
  return {
    appts,
    total: appts.length,
    confirmedAppts: appts.filter((a) => isConfirmedStatus(a.status)),
    toConfirmAppts: appts.filter((a) => !isConfirmedStatus(a.status)),
    notable: appts.filter((a) => a.highValue),
  }
}

// Patch a patient's status into an already-composed queue, so the pill updates
// the moment the write returns instead of waiting for a refetch. A patient can
// hold several of the day's slots and the status is a fact about the PERSON, so
// it lands on every row that names them.
export function applyPatientStatus(queue, patientId, status) {
  const pid = Number(patientId) || 0
  if (!queue || pid <= 0) return queue
  return deriveQueue(queue.appts.map((a) => (a.patientId === pid ? { ...a, patientStatus: status } : a)))
}

// Group appointments for the queue tabs. mode 'type' → by appointment type (rows
// sorted by doctor then time); mode 'doctor' → by doctor (rows by time).
// "ไม่ระบุ…" groups sort last.
export function groupAppts(appts, mode = 'type') {
  const groups = new Map()
  const keyOf = (a) => (mode === 'doctor' ? (a.doctor || 'ไม่ระบุแพทย์') : (a.type || 'ไม่ระบุประเภท'))
  for (const a of appts) {
    const k = keyOf(a)
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k).push(a)
  }
  const out = [...groups.entries()].map(([label, rows]) => ({
    label,
    rows: rows.slice().sort((x, y) => (mode === 'doctor'
      ? x.s - y.s
      : (x.doctor || '').localeCompare(y.doctor || '', 'th') || x.s - y.s)),
  }))
  out.sort((a, b) => {
    const au = a.label.startsWith('ไม่ระบุ'); const bu = b.label.startsWith('ไม่ระบุ')
    if (au !== bu) return au ? 1 : -1
    return a.label.localeCompare(b.label, 'th')
  })
  return out
}

/* -------------------------------------------------------------------------- */
/* Manager-authored vocabulary (SOP-FO-002 §3).                                */
/* -------------------------------------------------------------------------- */
// Daily Goal categories — the manager picks 2–3 SMART goals for the day.
export const GOAL_CATEGORIES = [
  { key: 'billing', label: 'ยอดรวม (Billing)', icon: '💰' },
  { key: 'nozero', label: 'No Zero Case', icon: '🚫' },
  { key: 'conversion', label: 'Conversion', icon: '🔄' },
  { key: 'promo', label: 'โปรโมชั่น / Upsale', icon: '💎' },
  { key: 'activity', label: 'Activity (เช่น Recall)', icon: '📞' },
]
export const goalCat = (key) => GOAL_CATEGORIES.find((c) => c.key === key) || { key, label: key || '—', icon: '🎯' }

// The CATEGORY is the goal. Detail and number are both optional: "No Zero Case"
// says everything on its own, and "ยอดรวม 150,000" needs no prose. The editor
// used to require `text`, then text-or-number, and either way silently dropped
// what a manager typed — the full-save POST overwrites daily_goals wholesale,
// so anything the filter rejects is gone for good.
export const isFilledGoal = (g) => Boolean((g?.category || '').trim())

// The one case the rule above would still swallow: a row with a detail or a
// number but no category picked. Never dropped silently — the editor blocks the
// save and points at the row.
export const goalNeedsCategory = (g) => (
  !(g?.category || '').trim() && Boolean((g?.text || '').trim() || (g?.target || '').trim())
)
export const filledGoals = (list) => (Array.isArray(list) ? list.filter(isFilledGoal) : [])

// What a goal reads as on one line: its own text, or the category name when the
// number is the whole goal.
export const goalLine = (g) => ((g?.text || '').trim() || goalCat(g?.category).label)

// How a goal's number reads on screen. The manager types it free-form ("50000",
// "3 เคส", "150,000 บาท"), so this only groups bare integer runs of 4+ digits —
// 50000 → 50,000 — and leaves everything else exactly as typed. A run already
// preceded by a "." or a "," is part of a number someone already formatted, so
// it is left alone rather than grouped twice.
const group = (n) => n.replace(/\B(?=(\d{3})+$)/g, ',')
export const goalTarget = (v) => String(v ?? '').replace(
  /\d{4,}/g,
  (n, i, str) => (i > 0 && /[.,]/.test(str[i - 1]) ? n : group(n)),
)

// Does this stored huddle row hold anything a manager typed? Used to catch a
// blank full-save landing on top of a real huddle.
export const huddleHasContent = (h) => (
  filledGoals(h?.daily_goals).length > 0
  || (Array.isArray(h?.notes) && h.notes.some((n) => (n?.body || '').trim()))
  || Boolean((h?.announcements || '').trim())
)

// The 7 morning-agenda topics — offered as note headings, but freeform is fine.
// ประกาศ keeps its own `announcements` column on the huddle row (the read view
// gives it a dedicated 📢 block), but the editor exposes it as a reserved หัวข้อ
// so a manager types everything in one list instead of a separate box that does
// the same job. HuddleEditor.save() splits this topic back out to the column.
export const ANNOUNCEMENT_TOPIC = 'ประกาศ / โปรโมชั่น / เรื่องแจ้งจากส่วนกลาง'

export const NOTE_TOPICS = [
  ANNOUNCEMENT_TOPIC,
  'รีวิวคิวนัดของวัน',
  'Recall — ยืนยันนัด (W/U)',
  'Ticket ติดตาม',
  'Upsell Opportunities',
  'High-Billing Cases',
  'Special Attention',
  'ส่งเวร / Operational',
]

// Normalise the /api/huddle list response to a single row (or null).
export function firstHuddle(r) {
  const list = asList(r)
  return list.length ? list[0] : null
}

// ---- morning-huddle upsell plans -------------------------------------------
// A plan is keyed by its appointment; a queue row with no appointment id (rare —
// a walk-in stub) falls back to the patient so it still gets one plan, not many.
export const planKey = (appointmentId, patientId) =>
  (Number(appointmentId) > 0 ? `a${Number(appointmentId)}` : `p${Number(patientId) || 0}`)

// Bangkok calendar day (YYYY-MM-DD) of a Xano timestamp / date value.
export const bkkDay = (v) => {
  if (v == null || v === '') return ''
  if (typeof v === 'number') return new Date(v + 7 * 3600 * 1000).toISOString().slice(0, 10)
  return String(v).slice(0, 10)
}

// promotion_patient rows bucketed by `patientId|YYYY-MM-DD`. Keyed by day as well
// as patient because a range query (month view) spans many days: matching on the
// patient alone would let a sale on the 3rd mark a plan on the 20th as won.
export function groupSold(sold) {
  const m = new Map()
  for (const r of asList(sold)) {
    const k = `${Number(r.patient_id) || 0}|${bkkDay(r.Creation_Date)}`
    m.set(k, [...(m.get(k) || []), r])
  }
  return m
}

// Outcome is DERIVED, never stored: the plan worked if ANY of its promotions was
// actually assigned to that patient on the planned day. One hit out of several
// planned is a win — the team got something sold.
export function planOutcome(plan, soldIndex) {
  const want = new Set((plan?.promotion_id || []).map(Number))
  const key = `${Number(plan?.patient_id) || 0}|${bkkDay(plan?.plan_date)}`
  const hits = (soldIndex.get(key) || [])
    .map((r) => Number(r.promotion_id))
    .filter((id) => want.has(id))
  return { won: hits.length > 0, hits: new Set(hits) }
}
